import { useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Check, UserRoundPen } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Seccion } from '@/components/Card';
import { actualizarSettings, db, leerSettings } from '@/db/schema';
import { crearRutinasDePlantilla, leerRutinasEnLista, rutinasQueFaltan } from '@/db/rutinas';
import { mensajeDeError } from '@/db/tx';
import { PLANTILLAS_DE_RUTINAS, plantillaDe, type RutinaDePlantilla } from '@/datos/rutinas-precargadas';
import { textoDescanso, textoObjetivo } from '@/domain/entrenamiento';
import { modalidadDe, textoPausaYVariante } from '@/domain/modalidad';
import { textoDias } from '@/domain/sesiones';
import { ElegirPersona } from './Precargadas';

/**
 * Las rutinas precargadas del teléfono (Req. 10.8) en la modalidad que se
 * ve en Gym: en el gimnasio, la de Janeth o las sesiones A, B y C de
 * Fernando; en casa, la de Janeth (la de Fernando espera su revisión). Se
 * revisan antes de crearlas. Sin elegir de quién es el teléfono, lo pregunta.
 * Crear solo agrega lo que falta, sin cargas, y no cambia nada de lo que ya
 * está.
 */
export function PlantillaPage() {
  const navegar = useNavigate();
  const idTitulo = useId();
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const ejercicios = useLiveQuery(() => db.exercises.toArray(), []);
  const rutinas = useLiveQuery(
    async () => (await leerRutinasEnLista()).map((r) => ({ ...r.rutina, exerciseIds: r.exerciseIds })),
    [],
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (ajustes === undefined || ejercicios === undefined || rutinas === undefined) return null;

  const volver = { a: '/gym', texto: 'Gym', etiqueta: 'Volver a Gym' };
  const modalidad = modalidadDe({ modalidad: ajustes.modalidadGym });
  const persona = ajustes.rutinasDe ?? null;
  const plantilla = plantillaDe(persona, modalidad);

  async function cambiarPersona() {
    setError(null);
    try {
      await actualizarSettings({ rutinasDe: null });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  if (persona !== null && plantilla === null) {
    // Elegida la persona, pero todavía sin rutina de casa (la de Fernando espera su revisión)
    return (
      <div className="space-y-6">
        <PageHeader
          antetitulo="Rutinas precargadas"
          titulo="Tu rutina de casa todavía no está."
          descripcion={`Cuando ${PLANTILLAS_DE_RUTINAS[persona].nombrePersona} revise su archivo de casa, aparece aquí para revisarla y crearla. Tus rutinas del gimnasio no cambian.`}
          volver={volver}
        />
        {error !== null && (
          <Banner tono="alerta" titulo="No se pudo">
            {error}
          </Banner>
        )}
        <Button variante="fantasma" icono={UserRoundPen} onClick={() => void cambiarPersona()}>
          No es mi teléfono: elegir otra persona
        </Button>
      </div>
    );
  }

  if (plantilla === null) {
    return (
      <div className="space-y-6">
        <PageHeader
          antetitulo="Rutinas precargadas"
          titulo="¿De quién es este teléfono?"
          descripcion="Elige una vez y te mostramos sus rutinas para revisarlas y crearlas. Elegir no cambia ni borra nada."
          volver={volver}
        />
        <p id={idTitulo} className="sr-only">
          ¿De quién es este teléfono?
        </p>
        <ElegirPersona etiquetadoPor={idTitulo} />
      </div>
    );
  }

  const nombres = new Map(ejercicios.map((x) => [x.id, x.nombre]));
  const faltan = rutinasQueFaltan(rutinas, plantilla.rutinas);
  const idsQueFaltan = new Set(faltan.map((r) => r.id));
  const yaEstan = plantilla.rutinas.length - faltan.length;
  const lista = plantilla.modalidad === 'casa' ? 'Mis rutinas de casa' : 'Mis rutinas';

  async function crear() {
    if (guardando) return;
    setGuardando(true);
    setError(null);
    try {
      await crearRutinasDePlantilla(faltan);
      void navegar('/gym', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        antetitulo={plantilla.pagina.antetitulo}
        titulo={plantilla.pagina.titulo}
        descripcion={plantilla.pagina.descripcion}
        volver={volver}
      />

      <Banner tono="info" titulo={plantilla.aviso?.titulo ?? 'Sin cargas iniciales'}>
        {plantilla.aviso?.texto ??
          'La primera vez cada ejercicio dice "Primera vez". Lo que registres se guarda para la vez pasada.'}
      </Banner>

      {yaEstan > 0 && (
        <Banner tono={faltan.length === 0 ? 'exito' : 'info'} titulo={faltan.length === 0 ? `Ya están todas en ${lista}` : `Ya tienes ${yaEstan} de ${plantilla.rutinas.length}`}>
          {faltan.length === 0
            ? 'No se vuelven a crear aunque les hayas cambiado el nombre.'
            : 'Solo se agregan las que faltan. Tus rutinas y entrenamientos no cambian.'}
        </Banner>
      )}

      {plantilla.rutinas.map((r) => (
        <RutinaDeLaPlantilla
          key={r.id}
          rutina={r}
          creada={!idsQueFaltan.has(r.id)}
          lista={lista}
          nombres={nombres}
          descansoPredeterminado={ajustes.descansoPredeterminadoSeg}
        />
      ))}

      {plantilla.porConfirmar.length > 0 && (
        <Seccion
          titulo="Por confirmar"
          descripcion="Vienen en tu tabla y falta una aclaración: no adivinamos el ejercicio. Lo que todavía no está en tus sesiones se agrega cuando lo confirmes."
        >
          <ul className="divide-y divide-borde">
            {plantilla.porConfirmar.map((p) => (
              <li key={p.texto} className="py-3">
                <p className="text-[12px] text-texto-suave">{p.sesion}</p>
                <p className="text-[14px] font-medium text-texto">{p.texto}</p>
                <p className="text-[12px] leading-relaxed text-texto-suave">{p.pregunta}</p>
              </li>
            ))}
          </ul>
        </Seccion>
      )}

      <p className="text-[12px] leading-relaxed text-texto-suave">{plantilla.general}</p>

      {error !== null && (
        <Banner tono="alerta" titulo="No se crearon">
          {error}
        </Banner>
      )}
      <div className="grid gap-2">
        {faltan.length > 0 && (
          <Button icono={Check} tamano="grande" disabled={guardando} onClick={() => void crear()}>
            {guardando ? 'Creando…' : plantilla.pagina.boton}
          </Button>
        )}
        <Button variante="fantasma" icono={UserRoundPen} onClick={() => void cambiarPersona()}>
          No es mi rutina: elegir otra persona
        </Button>
      </div>
    </div>
  );
}

function RutinaDeLaPlantilla({
  rutina,
  creada,
  lista,
  nombres,
  descansoPredeterminado,
}: {
  rutina: RutinaDePlantilla;
  creada: boolean;
  /** "Mis rutinas" o "Mis rutinas de casa". */
  lista: string;
  nombres: ReadonlyMap<string, string>;
  descansoPredeterminado: number;
}) {
  const descanso = (seg: number | null) =>
    seg === null ? `${textoDescanso(descansoPredeterminado)} (predeterminado)` : textoDescanso(seg);
  return (
    <Seccion titulo={rutina.nombre} extra={creada ? `Ya está en ${lista}` : textoDias(rutina.dias) || 'Días variables'}>
      <ul className="divide-y divide-borde">
        {rutina.ejercicios.map((e, i) => {
          const enBiserie = e.biserie !== null;
          const siguienteEnBiserie = enBiserie && rutina.ejercicios[i + 1]?.biserie === e.biserie;
          return (
            <li key={`${rutina.id}-${e.exerciseId}`} className="py-3">
              <p className="flex items-baseline justify-between gap-3">
                <span className="text-[14px] font-medium text-texto">
                  {enBiserie ? `${e.biserie}${siguienteEnBiserie ? '1' : '2'} · ` : ''}
                  {nombres.get(e.exerciseId) ?? e.exerciseId}
                </span>
                <span className="whitespace-nowrap text-[13px] tabular-nums text-texto">
                  {textoObjetivo(e.series, e.repsMin, e.repsMax, e.porLado === true)}
                </span>
              </p>
              <p className="text-[12px] text-texto-suave">
                {enBiserie
                  ? siguienteEnBiserie
                    ? 'Biserie: sigue el siguiente sin descanso'
                    : `Descanso al terminar los dos: ${textoDescanso(e.descansoBiserieSeg ?? e.descansoSeg ?? descansoPredeterminado)} · por separado ${descanso(e.descansoSeg)}`
                  : `Descanso ${descanso(e.descansoSeg)}`}
                {textoPausaYVariante(e) === '' ? '' : ` · ${textoPausaYVariante(e)}`}
              </p>
              {e.nombreEnArchivo !== undefined && (
                <p className="text-[12px] text-texto-suave">En tu tabla: {e.nombreEnArchivo}</p>
              )}
              {e.notas !== null && <p className="mt-1 text-[12px] leading-relaxed text-texto-suave">{e.notas}</p>}
            </li>
          );
        })}
      </ul>
    </Seccion>
  );
}
